// src/components/CodeHeaderTutorialPage.js
import React, { useEffect, useRef, useState } from "react";
import "./CodeHeaderTutorialPage.css";
import { SlArrowRight } from "react-icons/sl";
import img1 from "./img/10114186_4382747.jpg";
import pic1 from "./img/pic (1).jpg";
import jsonFiles from "./jsonFiles";
import componentsMap from "./componentsMap";

const CodeHeaderTutorialPage = ({ cleanName }) => {
  const sidebarRef = useRef(null);
  const rightbarRef = useRef(null);
  const buttonsRef = useRef(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [activeSubKey, setActiveSubKey] = useState(null);
  const [activeSubSubKey, setActiveSubSubKey] = useState(null);
  const [isButtonsSticky, setIsButtonsSticky] = useState(false);
  const [sidebarLayer, setSidebarLayer] = useState("topics");

  const rightbarImages = [img1];
  const bannerImages = [pic1];
  const [rightbarIndex, setRightbarIndex] = useState(0);
  const [bannerIndex, setBannerIndex] = useState(0);

  const tutorialData = jsonFiles[cleanName] || null;

  // ---------------------- Auto-select FIRST topic ----------------------
  useEffect(() => {
    if (tutorialData?.topics && !selectedTopic) {
      const firstSection = Object.values(tutorialData.topics)[0];
      if (firstSection?.length > 0) {
        setSelectedTopic(firstSection[0]);
      }
    }
  }, [tutorialData, selectedTopic]);

  // ---------------------- Scroll handler ----------------------
  useEffect(() => {
    const handleScroll = () => {
      const buttonsDiv = buttonsRef.current;
      if (buttonsDiv) {
        const topOffset = buttonsDiv.getBoundingClientRect().top;
        const navbarHeight = 64;
        setIsButtonsSticky(topOffset <= navbarHeight);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ---------------------- Auto sliders ----------------------
  useEffect(() => {
    const interval = setInterval(
      () => setRightbarIndex((prev) => (prev + 1) % rightbarImages.length),
      3000,
    );
    return () => clearInterval(interval);
  }, [rightbarImages.length]);

  useEffect(() => {
    const interval = setInterval(
      () => setBannerIndex((prev) => (prev + 1) % bannerImages.length),
      2500,
    );
    return () => clearInterval(interval);
  }, [bannerImages.length]);

  // ---------------------- Auto-select first subtopic ----------------------
  useEffect(() => {
    if (selectedTopic && selectedTopic.sub_contant) {
      const firstKey = Object.keys(selectedTopic.sub_contant)[0];
      setActiveSubKey(firstKey);
      setActiveSubSubKey(null);
    } else {
      setActiveSubKey(null);
      setActiveSubSubKey(null);
    }
  }, [selectedTopic]);

  // ---------------------- Auto-select first sub-subtopic ----------------------
  useEffect(() => {
    if (
      selectedTopic &&
      activeSubKey &&
      typeof selectedTopic.sub_contant?.[activeSubKey] === "object"
    ) {
      const firstSubSub = Object.keys(
        selectedTopic.sub_contant[activeSubKey],
      )[0];
      setActiveSubSubKey(firstSubSub);
    }
  }, [activeSubKey, selectedTopic]);

  // ---------------------- Determine which component to render ----------------------
  let RenderedComponent = null;
  const ComponentSet = componentsMap[cleanName];

  if (selectedTopic && ComponentSet) {
    if (
      activeSubKey &&
      selectedTopic.sub_contant &&
      typeof selectedTopic.sub_contant[activeSubKey] === "string"
    ) {
      const comp = selectedTopic.sub_contant[activeSubKey];
      if (comp && ComponentSet[comp]) {
        RenderedComponent = ComponentSet[comp];
      }
    } else if (
      activeSubKey &&
      selectedTopic.sub_contant &&
      typeof selectedTopic.sub_contant[activeSubKey] === "object"
    ) {
      if (activeSubSubKey) {
        const subComp =
          selectedTopic.sub_contant[activeSubKey][activeSubSubKey];
        if (subComp && ComponentSet[subComp]) {
          RenderedComponent = ComponentSet[subComp];
        }
      }
    } else if (selectedTopic.content && ComponentSet[selectedTopic.content]) {
      RenderedComponent = ComponentSet[selectedTopic.content];
    }
  }

  // ---------------------- Collect all navigation items (including nested) ----------------------
  const flattenTopics = (topics) => {
    const result = [];
    Object.values(topics).forEach((topicArr) => {
      topicArr.forEach((topic) => {
        if (topic.sub_contant) {
          Object.entries(topic.sub_contant).forEach(([sub, val]) => {
            if (typeof val === "object") {
              Object.keys(val).forEach((subsub) => {
                result.push({ topic, sub, subsub });
              });
            } else {
              result.push({ topic, sub });
            }
          });
        } else {
          result.push({ topic });
        }
      });
    });
    return result;
  };

  const allItems = tutorialData?.topics
    ? flattenTopics(tutorialData.topics)
    : [];

  // ---------------------- Find current index ----------------------
  const currentIndex = allItems.findIndex((item) => {
    if (item.subsub)
      return (
        item.topic.name === selectedTopic?.name &&
        item.sub === activeSubKey &&
        item.subsub === activeSubSubKey
      );
    if (item.sub)
      return (
        item.topic.name === selectedTopic?.name && item.sub === activeSubKey
      );
    return item.topic.name === selectedTopic?.name;
  });

  // ---------------------- Navigation handlers ----------------------
  const handleNavigation = (direction) => {
    const newIndex =
      direction === "next"
        ? Math.min(currentIndex + 1, allItems.length - 1)
        : Math.max(currentIndex - 1, 0);

    const nextItem = allItems[newIndex];
    if (!nextItem) return;

    setSelectedTopic(nextItem.topic);
    setActiveSubKey(nextItem.sub || null);
    setActiveSubSubKey(nextItem.subsub || null);

    // Update sidebar layer
    if (nextItem.subsub) setSidebarLayer("subsubtopics");
    else if (nextItem.sub) setSidebarLayer("subtopics");
    else setSidebarLayer("topics");

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!tutorialData) {
    return (
      <div className="details-container">
        <h2>
          ❌ No JSON data found for <strong>{cleanName}</strong>
        </h2>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Mobile Menu */}
      {!sidebarOpen && (
        <button className="menu-btn" onClick={() => setSidebarOpen(true)}>
          <SlArrowRight />
        </button>
      )}

      {sidebarOpen && (
        <div className="overlay" onClick={() => setSidebarOpen(false)}></div>
      )}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`sidebar ${sidebarOpen ? "open" : ""}`}
      >
        <button className="close-btn" onClick={() => setSidebarOpen(false)}>
          ✖
        </button>

        {/* ✅ Topics Layer */}
        {sidebarLayer === "topics" && (
          <ul className="topic-list">
            {tutorialData.topics ? (
              Object.entries(tutorialData.topics).map(
                ([section, items], secIdx) => (
                  <li key={secIdx}>
                    <strong>{section}</strong>
                    <ul>
                      {items.map((topic, idx) => (
                        <li
                          key={idx}
                          className={
                            selectedTopic?.name === topic.name
                              ? "active-topic-btn"
                              : ""
                          }
                          onClick={() => {
                            setSelectedTopic(topic);
                            setSidebarOpen(false);
                            if (
                              topic.sub_contant &&
                              Object.keys(topic.sub_contant).length > 0
                            ) {
                              setSidebarLayer("subtopics");
                            }
                          }}
                        >
                          {topic.name}
                        </li>
                      ))}
                    </ul>
                  </li>
                ),
              )
            ) : (
              <li>No topics available</li>
            )}
          </ul>
        )}

        {/* ✅ Subtopics Layer */}
        {sidebarLayer === "subtopics" && selectedTopic && (
          <div className="subtopic-layer">
            <button
              className="back-btn"
              onClick={() => setSidebarLayer("topics")}
            >
              ← Back
            </button>
            <h3 className="subtopic-title">{selectedTopic.name}</h3>
            {selectedTopic.sub_contant ? (
              Object.keys(selectedTopic.sub_contant).map((key) => (
                <button
                  key={key}
                  className={activeSubKey === key ? "active-sub-btn" : ""}
                  onClick={() => {
                    setActiveSubKey(key);
                    if (
                      typeof selectedTopic.sub_contant[key] === "object" &&
                      !Array.isArray(selectedTopic.sub_contant[key])
                    ) {
                      setSidebarLayer("subsubtopics");
                    } else {
                      setSidebarOpen(false);
                    }
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  {key}
                </button>
              ))
            ) : (
              <p>No subcontent available</p>
            )}
          </div>
        )}

        {/* ✅ Sub-subtopics Layer */}
        {sidebarLayer === "subsubtopics" && selectedTopic && activeSubKey && (
          <div className="subtopic-layer">
            <button
              className="back-btn"
              onClick={() => setSidebarLayer("subtopics")}
            >
              ← Back
            </button>
            <h3 className="subtopic-title">{activeSubKey}</h3>
            {selectedTopic.sub_contant[activeSubKey] ? (
              Object.keys(selectedTopic.sub_contant[activeSubKey]).map(
                (sub) => (
                  <button
                    key={sub}
                    className={
                      activeSubSubKey === sub ? "active-subsub-btn" : ""
                    }
                    onClick={() => {
                      setActiveSubSubKey(sub);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    {sub}
                  </button>
                ),
              )
            ) : (
              <p>No deeper content available</p>
            )}
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {/* Banner */}
        <div className="banner-slider">
          <img
            src={bannerImages[bannerIndex]}
            alt="Banner Slide"
            className="banner-slide-image"
          />
        </div>

        {/* 🔹 Top Navigation Buttons */}
        <div ref={buttonsRef} className="Next_Previous_button_container">
          <button
            onClick={() => handleNavigation("prev")}
            disabled={currentIndex <= 0}
            className="Previous_button"
          >
            ◀ Previous
          </button>
          <button
            onClick={() => handleNavigation("next")}
            disabled={currentIndex >= allItems.length - 1}
            className="Next_button"
          >
            Next ▶
          </button>
        </div>

        {/* Render component */}
        {RenderedComponent ? (
          <RenderedComponent />
        ) : (
          <div>
            <h2>{selectedTopic?.name}</h2>
            <p>No extra content for this topic.</p>
          </div>
        )}

        {/* 🔹 Bottom Navigation Buttons */}
        <div className="Next_Previous_button_container">
          <button
            onClick={() => handleNavigation("prev")}
            disabled={currentIndex <= 0}
            className="Previous_button"
          >
            ◀ Previous
          </button>
          <button
            onClick={() => handleNavigation("next")}
            disabled={currentIndex >= allItems.length - 1}
            className="Next_button"
          >
            Next ▶
          </button>
        </div>
      </main>

      {/* Rightbar */}
      <aside ref={rightbarRef} className="rightbar">
        <div className="ad-box">
          <h3>Get Certificate</h3>
          <p>For Any Courses</p>
          <p className="price">
            <span className="old">Rs 1000</span>{" "}
            <span className="new">Rs 500</span>
          </p>
          <button className="btn">Save 50% now!</button>
        </div>
        <div className="slider">
          <img
            src={rightbarImages[rightbarIndex]}
            alt="Rightbar Slide"
            className="slide-image"
          />
        </div>
      </aside>
    </div>
  );
};

export default CodeHeaderTutorialPage;
